import React, { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';

const Section = ({ number, title, id, children }) => (
  <section id={id} className="mb-10 scroll-mt-6">
    <h2 className="font-heading text-xl font-semibold text-primary mb-4">
      {number}. {title}
    </h2>
    {children}
  </section>
);

const SubHeading = ({ children }) => (
  <h3 className="font-heading font-semibold text-text-main mb-2 mt-5">{children}</h3>
);

const BulletList = ({ items }) => (
  <ul className="space-y-1.5 ml-1 mb-3">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2.5 text-text-main text-sm leading-relaxed">
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Para = ({ children }) => (
  <p className="text-text-main text-sm leading-relaxed mb-3">{children}</p>
);

const Email = ({ address }) => (
  <a href={`mailto:${address}`} className="text-primary hover:underline">{address}</a>
);

const PageLink = ({ onNavigate, to, children }) =>
  onNavigate ? (
    <button type="button" onClick={() => onNavigate(to)} className="text-primary hover:underline">
      {children}
    </button>
  ) : (
    <span>{children}</span>
  );

const TermsPage = ({ onNavigate }) => {
  // Deep links such as /terms#events jump straight to that section
  useEffect(() => {
    const id = window.location.hash.replace('#', '');
    if (!id) return undefined;
    const timer = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="h-1 w-full" style={{ background: 'linear-gradient(to right, #4AADA8, #A99CC8)' }} aria-hidden="true" />

      <div className="max-w-2xl mx-auto px-6 py-12">
        {onNavigate && (
          <button
            onClick={() => onNavigate('feed')}
            aria-label="Back to Sirati"
            className="flex items-center gap-2 text-sm text-text-muted hover:text-primary transition-colors mb-10 min-h-[44px]"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back to Sirati
          </button>
        )}

        <header className="text-center mb-12">
          <p className="font-heading text-3xl font-bold tracking-wide text-primary mb-1">SIRATI</p>
          <h1 className="font-heading text-2xl font-semibold mb-2" style={{ color: '#A99CC8' }}>Terms &amp; Conditions</h1>
          <p className="text-xs text-text-muted">Effective Date: October 2026 &nbsp;|&nbsp; Last Updated: October 2026</p>
        </header>

        <div className="h-px mb-10" style={{ background: 'linear-gradient(to right, transparent, #4AADA820, #A99CC830, #4AADA820, transparent)' }} aria-hidden="true" />

        <div>

          <Section number="1" title="Agreement to These Terms">
            <Para>
              Welcome to Sirati (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). Sirati is a faith-centered community for Muslim singles focused on personal growth and marriage readiness, available at www.sirati.co and through our apps.
            </Para>
            <Para>
              These Terms &amp; Conditions (&ldquo;Terms&rdquo;) are a legal agreement between you and Sirati. By creating an account, signing in, or using Sirati in any way, you agree to these Terms and to our{' '}
              <PageLink onNavigate={onNavigate} to="privacy">Privacy Policy</PageLink>. If you do not agree, do not use Sirati.
            </Para>
          </Section>

          <Section number="2" title="Who Can Use Sirati: Adults Only (18+)">
            <Para>
              <strong>You must be at least 18 years old</strong> and legally an adult able to enter into a binding agreement to create an account or use Sirati. By creating an account, you confirm that you meet this requirement.
            </Para>
            <Para>
              Sirati is not a platform for minors. Conversations and content in our community may include adult subject matter that is not appropriate for anyone under 18.
            </Para>
            <BulletList items={[
              'If we learn or reasonably believe that someone joined Sirati while under 18, we will ban and delete their account.',
              'We may ask for proof of age at any time, and may suspend an account until we receive it.',
              'Giving us a false age, or helping a minor access Sirati, is a violation of these Terms.',
              'If you are a parent or guardian and believe a minor is using Sirati, please contact us right away at salam@sirati.co.',
            ]} />
            <Para>
              See also our <PageLink onNavigate={onNavigate} to="child-safety">Child Safety Standards</PageLink>.
            </Para>
          </Section>

          <Section number="3" title="Your Account">
            <BulletList items={[
              'Give us accurate information when you sign up, including your email and whether you are a brother or sister. Some features depend on it.',
              'One person, one account. Do not share your login or let anyone else use your account.',
              'Keep your password secure. You are responsible for activity on your account.',
              'Do not impersonate another person or misrepresent who you are.',
              'Tell us right away at salam@sirati.co if you think your account has been accessed without your permission.',
            ]} />
          </Section>

          <Section number="4" title="Community Standards">
            <Para>
              Sirati is meant to be a safe, respectful, and uplifting space. When you use it, you agree not to:
            </Para>
            <BulletList items={[
              'Harass, bully, threaten, stalk, shame, or demean other members, or use hateful or discriminatory language',
              'Post or send sexually explicit material, or make unwanted sexual or romantic advances',
              'Share anything illegal, fraudulent, or deceptive, including scams',
              'Send money to, or ask for money from, other members',
              'Post content that exploits, endangers, or sexualizes anyone',
              'Advertise, recruit, spam, or solicit members for products, services, or off-platform arrangements without our written permission',
              'Post affiliate links, referral links, promo codes, sponsored content, or ads of any kind (the only exception is Sirati\u2019s own official invite link)',
              'Share another person’s private information without their consent',
              'Use bots, scrapers, or automated tools, or try to bypass paywalls, limits, or security',
              'Interfere with the platform, or attempt to access accounts or data that are not yours',
              'Use a new account to get around a suspension or ban',
            ]} />
            <Para>
              You can report content or members using the Report option in the app, and block members you do not want to hear from.
            </Para>
          </Section>

          <Section number="5" title="Keeping the Community Private">
            <Para>
              What is shared inside Sirati is not to be shared outside of the community. Members trust this community with personal conversations, and that trust is central to what we are building.
            </Para>
            <Para>
              You may not copy, screenshot, record, download, forward, repost, publish, or otherwise share any of the following with anyone outside the Sirati community:
            </Para>
            <BulletList items={[
              'Posts, comments, and replies',
              'Direct messages and mentor conversations',
              'Live events and livestreams, including any recordings',
              'Lessons, Spaces, resources, downloads, and other learning materials',
              'Member profiles, names, photos, and personal details',
              'Any other Sirati content or user-created content inside Sirati',
            ]} />
            <Para>
              This applies wherever the sharing happens, including social media, group chats, articles, videos, and AI tools. Do not use Sirati content to train or feed any AI system.
            </Para>
            <SubHeading>Limited exceptions</SubHeading>
            <BulletList items={[
              'Your own content. You may share things you created yourself, as long as you do not include other members’ replies, messages, or personal details.',
              'Reporting harm. You may share content with Sirati, law enforcement, or an attorney as needed to report abuse, harassment, threats, or illegal activity, or to protect yourself or others from harm.',
              'Legal requirements. You may disclose content if the law requires it.',
            ]} />
            <Para>
              Breaking this rule can lead to content removal, suspension, or a permanent ban, and we may take legal action where appropriate.
            </Para>
          </Section>

          <Section number="6" title="Content and Copyright">
            <SubHeading>Sirati content</SubHeading>
            <Para>
              All content that Sirati creates and publishes, including lessons, courses, resources, text, graphics, audio, video, livestream recordings, custom emoji, designs, and our name and logo (&ldquo;Sirati Content&rdquo;), is protected by copyright and other intellectual property laws from the moment it is published. &copy; {new Date().getFullYear()} Sirati. All rights reserved.
            </Para>
            <Para>
              We give you a limited, personal, non-commercial, non-transferable license to view and use Sirati Content inside Sirati. You may not copy, reproduce, distribute, sell, modify, or create derivative works from it, or use the Sirati name or logo, without our written permission.
            </Para>
            <SubHeading>Your content</SubHeading>
            <Para>
              You own the content you create and post on Sirati. By posting it, you give Sirati a non-exclusive, worldwide, royalty-free license to host, store, reproduce, display, and distribute your content as needed to operate, secure, and improve Sirati, and to show it to other members according to your visibility settings.
            </Para>
            <BulletList items={[
              'You promise that you have the right to post what you post, and that it does not infringe anyone else’s rights.',
              'Do not post copyrighted material you do not have permission to use.',
              'If you delete content, it is removed from view. Posts may remain in anonymized form after account deletion, as explained in our Privacy Policy. You can ask us to delete them at salam@sirati.co.',
              'Other members’ content belongs to them. Seeing it on Sirati does not give you the right to use it elsewhere (see Section 5).',
            ]} />
            <SubHeading>Copyright complaints</SubHeading>
            <Para>
              If you believe content on Sirati infringes your copyright, email <Email address="salam@sirati.co" /> with: a description of the work; where the content appears on Sirati; your name and contact details; a statement that you believe in good faith the use is not authorized; and a statement, under penalty of perjury, that your information is accurate and that you are the owner or authorized to act for the owner. Include your signature (typed is fine).
            </Para>
          </Section>

          <Section number="7" title="Paid Features and Billing">
            <Para>
              Some features, such as Sirati+ and the Mentor feature, may require a paid subscription or add-on. Prices, billing periods, and usage limits (for example, how many mentor questions are included each month) are shown in the app before you pay.
            </Para>
            <BulletList items={[
              'Payments are processed securely by Stripe. We do not store your card details.',
              'Monthly and yearly Sirati+ subscriptions renew automatically each billing period until you cancel.',
              'Lifetime Sirati+ is a one-time payment. It does not renew, and there is nothing to cancel. It gives you Sirati+ access, including every item in the Library, for as long as Sirati offers Sirati+.',
              'You can cancel any time from your Profile. Cancellation takes effect at the end of your current billing period, and you keep access until then.',
              'Sirati+ and the Mentor add-on are separate subscriptions and are cancelled separately.',
              'Except where the law requires otherwise, payments are non-refundable and we do not give partial refunds for unused time. If you believe you were charged in error, contact salam@sirati.co.',
              'Event tickets are handled differently. Refunds for event tickets are covered in Section 20 (Events), which applies instead of the rule above.',
              'Library purchases are handled differently. Refunds for Library items are covered in Section 21 (Library and Digital Resources), which applies instead of the rule above.',
              'We may change prices or features with advance notice. Changes apply from your next billing period.',
              'If your account is banned for violating these Terms, you are not entitled to a refund.',
            ]} />
          </Section>

          <Section number="8" title="Email and Notifications">
            <Para>
              When you create an account, you are added to Sirati&rsquo;s email list. We use it to send community announcements, product updates, events, and resources.
            </Para>
            <BulletList items={[
              'You can unsubscribe from community announcements at any time using the unsubscribe link at the bottom of any announcement email, or by emailing salam@sirati.co.',
              'Unsubscribing stops announcements only. We will still send essential messages about your account, such as sign-up confirmation, password resets, security alerts, billing receipts, and notices about these Terms or our policies.',
              'Push notifications are optional. You can turn them off at any time in your device or browser settings.',
            ]} />
            <Para>
              By using Sirati, you agree that we may communicate with you electronically, and that electronic notices satisfy any legal requirement that communications be in writing.
            </Para>
          </Section>

          <Section number="9" title="Guidance, Not Professional Advice">
            <Para>
              Sirati&rsquo;s lessons, mentors, and members share perspectives on faith, marriage, family, finances, and emotional well-being. This content is for education and support only.
            </Para>
            <BulletList items={[
              'It is not a formal religious ruling (fatwa) and does not replace guidance from a qualified scholar.',
              'It is not legal, financial, medical, or mental-health advice, and is not professional counseling or therapy.',
              'Mentors are members of our community. Unless they say otherwise, they are not licensed professionals, and their answers reflect their own views.',
              'Tools like the Marriage Readiness Score and the financial tracker are for personal reflection. They are not predictions, guarantees, or financial advice.',
              'Views shared by members, mentors, or guests are theirs and do not necessarily reflect Sirati’s.',
            ]} />
            <Para>
              <strong>Sirati is not an emergency or crisis service.</strong> If you or someone else is in immediate danger, call 911. In the U.S., you can call or text 988 to reach the Suicide &amp; Crisis Lifeline.
            </Para>
          </Section>

          <Section number="10" title="Safety and Member Interactions">
            <BulletList items={[
              'We do not run background checks and cannot verify members’ identities, marital status, or claims about themselves.',
              'You are responsible for your own interactions with other members. Be thoughtful about the personal information you share.',
              'Never send money to someone you meet on Sirati. Doing so violates these Terms.',
              'If you decide to take a conversation beyond Sirati or meet someone in person, take sensible precautions, such as involving family or a trusted person and meeting in public.',
              'Sirati is a community and learning platform. We do not guarantee that you will find a spouse or any particular outcome.',
            ]} />
            <Para>
              If someone makes you feel unsafe, please report them in the app or email salam@sirati.co.
            </Para>
          </Section>

          <Section number="11" title="Moderation, Suspension, and Termination">
            <Para>
              We may remove content, issue warnings, suspend accounts, or permanently ban members who violate these Terms or put the community at risk. We decide this at our discretion, and may act without notice where needed for safety or legal reasons.
            </Para>
            <Para>
              If you believe we made a mistake, contact <Email address="salam@sirati.co" /> and we will review it.
            </Para>
            <Para>
              You can stop using Sirati and delete your account at any time. See <PageLink onNavigate={onNavigate} to="delete-data">how to delete your data</PageLink>. When your account ends, your right to use Sirati ends. Sections that by their nature should continue (including 5, 6, 14, 15, 16, and 17) continue after termination.
            </Para>
          </Section>

          <Section number="12" title="Third-Party Services and Links">
            <Para>
              Sirati relies on third-party services such as payment processing, sign-in, and hosting, and may link to outside websites. Those services have their own terms and privacy practices, and we are not responsible for them. Any referral or affiliate programs we offer may have their own additional rules.
            </Para>
            <Para>
              If you are a mentor, the <PageLink onNavigate={onNavigate} to="mentor-terms">Mentor Agreement</PageLink> also applies to you.
            </Para>
          </Section>

          <Section number="13" title="Affiliate Links and Advertising">
            <Para>
              Sirati may show advertisements, sponsored content, and affiliate links inside the community. If you click an affiliate link or an ad and then make a purchase or sign up, Sirati may earn a commission or other compensation.
            </Para>
            <BulletList items={[
              'Ads in Sirati are labeled as Sponsored, and affiliate links are identified as affiliate links where they appear.',
              'Earning compensation does not mean Sirati endorses, guarantees, or is responsible for any product, service, or advertiser. Please do your own research before you buy or sign up.',
              'Members may not post their own affiliate links, referral links, promo codes, sponsored content, or ads in the community (see Section 4). The only exception is Sirati\u2019s own official invite link.',
            ]} />
          </Section>

          <Section number="14" title="Disclaimers">
            <Para>
              Sirati is provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo; To the fullest extent the law allows, we make no promises that Sirati will be uninterrupted, error-free, or secure, and we disclaim all warranties, whether express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </Para>
            <Para>
              We do not control and are not responsible for what members post or say, or for how members behave, on or off Sirati.
            </Para>
            <Para>
              We do not control the venues, partner businesses, or other attendees at events, and we are not responsible for how they behave.
            </Para>
          </Section>

          <Section number="15" title="Limitation of Liability">
            <Para>
              To the fullest extent the law allows, Sirati and its owners, officers, employees, and partners will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for lost profits, data, or opportunities, arising out of your use of Sirati.
            </Para>
            <Para>
              Our total liability for any claim relating to Sirati will not exceed the greater of the amount you paid us in the 12 months before the claim or $100. Some places do not allow certain limitations, so parts of this section may not apply to you.
            </Para>
          </Section>

          <Section number="16" title="Indemnification">
            <Para>
              You agree to defend and hold Sirati harmless from claims, losses, and expenses (including reasonable legal fees) arising from your content, your violation of these Terms or the law, or your violation of another person&rsquo;s rights.
            </Para>
          </Section>

          <Section number="17" title="Governing Law">
            <Para>
              These Terms are governed by the laws of the Commonwealth of Virginia, United States, without regard to conflict-of-law rules. Any dispute that cannot be resolved informally will be brought in the state or federal courts located in Virginia, and you agree to their jurisdiction.
            </Para>
          </Section>

          <Section number="18" title="Changes to These Terms">
            <Para>
              We may update these Terms from time to time. We will post the updated version here, change the &ldquo;Last Updated&rdquo; date, and for significant changes notify you in the app or by email. By continuing to use Sirati after changes take effect, you accept the updated Terms.
            </Para>
          </Section>

          <Section number="19" title="General">
            <BulletList items={[
              'These Terms, together with our Privacy Policy, Child Safety Standards, and (for mentors) the Mentor Agreement, are the entire agreement between you and Sirati about using Sirati.',
              'If any part of these Terms is found unenforceable, the rest stays in effect.',
              'If we do not enforce a part of these Terms, that does not mean we give it up.',
              'You may not transfer your account or rights under these Terms. We may transfer ours as part of a merger, sale, or reorganization.',
            ]} />
          </Section>

          <Section number="20" title="Events" id="events">
            <Para>
              Sirati hosts in-person and online events for members, sometimes together with partner venues or businesses. These terms apply when you register for or attend an event. The event page shows the date, time, city, price, and number of spots. The exact address or online link is shown after you register.
            </Para>

            <SubHeading>Who can attend</SubHeading>
            <BulletList items={[
              'Events are for Sirati members who are 18 or older. You need a Sirati account to register.',
              'A ticket is for one person and cannot be transferred or resold.',
              'Some events are separated by gender, or have a separate limit for brothers and sisters. We use the gender on your profile for this, so please keep it accurate.',
            ]} />

            <SubHeading>Tickets and payment</SubHeading>
            <BulletList items={[
              'The price is shown before you pay. Payments are processed securely by Stripe, as described in Section 7.',
              'Sirati+ members may get early access to tickets and a discount. These are shown on the event page and at checkout.',
              'When you start to pay, we hold your seat for 10 minutes. Your seat is confirmed only when your payment succeeds.',
              'Free events still need registration, and the rules below still apply.',
            ]} />

            <SubHeading>Cancellations and refunds</SubHeading>
            <BulletList items={[
              'You can cancel and get a full refund of your ticket price up to 72 hours before the event starts. Each event page shows the exact deadline. If an event uses a different window, the event page controls.',
              'After that deadline we do not give refunds. If we cancel an event, you get a full refund.',
              'To cancel, email salam@sirati.co.',
              'If we cancel an event, you get a full refund. If we make a major change to the date or venue, you can ask for a refund.',
              'Refunds go back to the card you used and can take several business days to appear. We do not take the card processing fee out of your refund.',
              'We are not responsible for travel, accommodation, or other costs you spend to attend, including if an event changes or is cancelled.',
            ]} />

            <SubHeading>Behavior at events</SubHeading>
            <BulletList items={[
              'Treat every attendee with respect and follow the Community Standards in Section 4. In person, that also means no harassment, unwanted contact, or pressure on anyone to share personal details. Accept it when someone says no, and follow the venue\u2019s rules and the organizers\u2019 instructions.',
              'No outside food or drink at events.',
              'No selling, recruiting, or promoting a business or referral link at an event without our written permission (Section 13 applies).',
              'Do not record, photograph, or post about other attendees without their consent.',
              'We can ask anyone to leave, and can restrict someone from future events, if we believe they are not following these rules or are making others feel unsafe. In that case there is no refund.',
              'If something worries you at an event, tell an organizer on the spot or email salam@sirati.co afterward.',
            ]} />

            <SubHeading>Safety</SubHeading>
            <BulletList items={[
              'Events are held in public venues with partners. Section 10 applies: we do not run background checks and cannot verify who attends or what they say about themselves.',
              'You attend at your own risk. Use sensible precautions, such as telling someone where you are going and arranging your own travel.',
            ]} />
            <Para>
              To the fullest extent the law allows, you understand that attending an in-person event involves some risk, and you accept that risk. Sirati&rsquo;s liability for events is limited as described in Sections 14 and 15.
            </Para>

            <SubHeading>Venues and partners</SubHeading>
            <BulletList items={[
              'Some events are hosted with partner venues. Their rules apply while you are there. We are not responsible for what a venue or its staff does.',
              'The exact location is for registered attendees only. Please do not post it publicly or share it with people who have not registered (see Section 5).',
            ]} />

            <SubHeading>Photos and video</SubHeading>
            <BulletList items={[
              'We may take photos or video at events and use them on Sirati\u2019s website, social media, and marketing.',
              'You can opt out of photos and video when you buy your ticket, or by telling an organizer when you check in. We will do our best to make sure our photographers know, for example with a different-colour name tag.',
            ]} />

            <SubHeading>Online events</SubHeading>
            <BulletList items={[
              'Your online link is personal to your registration. Please do not share it.',
              'Some online events are recorded. If an event will be recorded, we will say so on the event page or at the start of the session. You can keep your camera and microphone off.',
            ]} />

            <SubHeading>Changes</SubHeading>
            <Para>
              We may change an event&rsquo;s time, venue, or format, for example because of weather or low numbers. We will tell you by email or notification as early as we can.
            </Para>
          </Section>

          <Section number="21" title="Library and Digital Resources" id="library">
            <Para>
              The Sirati Library offers downloadable guides, tools, and other digital resources for members. Some items are free, some come with Sirati+, and some are sold separately as one-time purchases. Each item shows who it is for and its price before you pay.
            </Para>

            <SubHeading>Who gets what</SubHeading>
            <BulletList items={[
              'Free items are available to every member with an account.',
              'Sirati+ items are included with a paid Sirati+ plan. If you stop being a Sirati+ member, you can no longer download these items until you rejoin.',
              'Monthly and yearly Sirati+ members get 50% off items sold separately.',
              'Lifetime Sirati+ members get every item in the Library, including items sold separately, at no extra cost, for as long as Sirati offers Sirati+.',
              'An item you buy stays yours. You can download it again at any time while your account is active.',
            ]} />

            <SubHeading>How you may use Library items</SubHeading>
            <BulletList items={[
              'Library items are for your own personal, non-commercial use.',
              'Do not share, resell, post, or distribute them, including to other members, and do not remove names or notices from them.',
              'Library items belong to Sirati or its creators. Buying or downloading one does not transfer any ownership.',
              'Downloads are for signed-in members only. Download links expire after a short time, so request a new one from the Library whenever you need it.',
            ]} />

            <SubHeading>Payment and refunds</SubHeading>
            <BulletList items={[
              'Payments are processed securely by Stripe, as described in Section 7. We do not store your card details.',
              'A purchase is a one-time payment. It does not renew.',
              'A purchase is non-refundable once you have downloaded the item.',
              'If you have not downloaded it, you can ask for a full refund within 7 days of buying it by emailing salam@sirati.co. Refunds go back to the card you used and can take several business days to appear.',
              'Free items and items included with your plan have no separate price to refund.',
              'If we permanently remove an item you bought and can no longer provide it, we will refund you.',
            ]} />

            <SubHeading>Changes</SubHeading>
            <BulletList items={[
              'We may update, replace, or retire Library items. We will tell you if a change affects something you bought.',
              'Library items are guidance and learning material. Section 9 (Guidance, Not Professional Advice) applies to them.',
            ]} />
          </Section>

          <Section number="22" title="Contact Us">
            <Para>Questions about these Terms, or need to reach us about a copyright, safety, or account concern?</Para>
            <div className="mt-3 p-4 rounded-arch-lg border border-primary/20 bg-primary/5 text-sm text-text-main space-y-1">
              <p><span className="font-medium text-primary">Sirati</span></p>
              <p>General and legal: <Email address="salam@sirati.co" /></p>
              <p>Privacy requests: <Email address="privacy@sirati.co" /></p>
              <p>Website: <a href="https://www.sirati.co" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.sirati.co</a></p>
            </div>
          </Section>

        </div>

        <div className="mt-12 pt-8 border-t border-border text-center">
          <p className="text-xs text-text-muted">
            &copy; {new Date().getFullYear()} Sirati. All Rights Reserved.
          </p>
          {onNavigate && (
            <button
              onClick={() => onNavigate('feed')}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white transition-opacity hover:opacity-90 min-h-[44px]"
              style={{ background: 'linear-gradient(135deg, #4AADA8, #A99CC8)' }}
            >
              Return to Sirati
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
